import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { useTheme, type Theme } from "@mui/material/styles";
import OrgLogo from "../components/organizations/OrgLogo";
import PageTitleIcon from "../components/PageTitleIcon";
import { APP_FONT_FAMILY } from "../constants/fonts";
import { PERM_ORG_WRITE } from "../auth";
import { useAuthPermissions } from "../hooks/useAuthPermissions";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import {
  OrganizationSchema,
  OrganizationType,
  getOrganizationTypeLabel,
} from "../types/organization.type";

function getOrganizationTypeColor(type: OrganizationType, theme: Theme) {
  switch (type) {
    case OrganizationType.RECYCLER:
      return theme.palette.success.main;
    case OrganizationType.WASTE_OUTLET:
      return theme.palette.primary.main;
    case OrganizationType.TRUCK:
      return theme.palette.warning.main;
    case OrganizationType.CHARITY:
      return theme.palette.info.main;
    default:
      return theme.palette.grey[500];
  }
}

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function SectionCard({ icon, title, children }: SectionCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: isDark
          ? `0 8px 24px ${alpha("#000", 0.28)}`
          : `0 8px 24px ${alpha("#0f172a", 0.05)}`,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2.25, sm: 3 }, "&:last-child": { pb: { xs: 2.25, sm: 3 } } }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            mb: 2.25,
            pb: 1.75,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontFamily: APP_FONT_FAMILY,
              fontWeight: 700,
              fontSize: "1.05rem",
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.75rem",
          fontWeight: 700,
          color: "text.secondary",
          letterSpacing: "0.02em",
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.95rem",
          fontWeight: 500,
          color: "text.primary",
          wordBreak: "break-word",
        }}
      >
        {value?.trim() ? value : "—"}
      </Typography>
    </Box>
  );
}

const OrganizationDetails: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { fetchOrganizationById, error } = useOrganizationStore();
  const { isReadOnly } = useAuthPermissions();
  const { canAccessPermission } = useAuthStore();

  const [org, setOrg] = useState<OrganizationSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const canEditOrg = useMemo(
    () =>
      organizationId && !isReadOnly
        ? canAccessPermission(PERM_ORG_WRITE(organizationId), undefined, organizationId)
        : false,
    [canAccessPermission, organizationId, isReadOnly]
  );

  const loadOrg = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setLoadError(null);
    const result = await fetchOrganizationById(organizationId);
    setLoading(false);
    if (!result) {
      setLoadError(error ?? t("organizations.detailsLoadFailed"));
      setOrg(null);
      return;
    }
    setOrg(result);
  }, [organizationId, fetchOrganizationById, error, t]);

  useEffect(() => {
    void loadOrg();
  }, [loadOrg]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !org) {
    return (
      <Box sx={{ p: 3, maxWidth: 960, mx: "auto" }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/organizations")}
          sx={{ mb: 2, textTransform: "none", borderRadius: "12px", fontWeight: 600 }}
        >
          {t("organizations.backToList")}
        </Button>
        <Alert severity="error" sx={{ borderRadius: "12px" }}>
          {loadError ?? t("organizations.detailsNotFound")}
        </Alert>
      </Box>
    );
  }

  const industries = Array.isArray(org.data?.industry)
    ? (org.data.industry as string[])
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: "auto" }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: "16px",
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12)}`,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 50%, ${theme.palette.background.paper} 100%)`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 10px 28px ${alpha("#000", 0.3)}`
              : `0 10px 28px ${alpha("#0f172a", 0.06)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton
              onClick={() => navigate("/organizations")}
              aria-label={t("organizations.backToList")}
              sx={{
                color: "text.secondary",
                bgcolor: alpha(
                  theme.palette.common.black,
                  theme.palette.mode === "dark" ? 0.2 : 0.04,
                ),
                "&:hover": {
                  bgcolor: alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.32 : 0.08,
                  ),
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <PageTitleIcon icon={<BusinessOutlinedIcon />} />
            <Typography
              component="h1"
              sx={{
                fontFamily: APP_FONT_FAMILY,
                fontWeight: 800,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                color: "text.primary",
              }}
            >
              {t("organizations.detailsTitle")}
            </Typography>
          </Stack>
          {canEditOrg && (
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={() =>
                navigate("/organizations", { state: { editOrgId: org.id } })
              }
              sx={{
                textTransform: "none",
                borderRadius: "12px",
                px: 2.5,
                py: 1,
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {t("organizations.edit")}
            </Button>
          )}
        </Stack>
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: "16px",
          mb: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 8px 24px ${alpha("#000", 0.28)}`
              : `0 8px 24px ${alpha("#0f172a", 0.05)}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <OrgLogo
              name={org.name}
              logoUrl={org.logo_url}
              size={80}
              onBroken={() => void loadOrg()}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: APP_FONT_FAMILY,
                  fontWeight: 800,
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                  color: "text.primary",
                }}
              >
                {org.name}
              </Typography>
              <Chip
                label={getOrganizationTypeLabel(org.organization_type)}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: getOrganizationTypeColor(org.organization_type, theme),
                  color: theme.palette.getContrastText(
                    getOrganizationTypeColor(org.organization_type, theme),
                  ),
                  fontWeight: 600,
                  borderRadius: "8px",
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Stack spacing={2.5}>
        <SectionCard
          icon={<BusinessOutlinedIcon fontSize="small" />}
          title={t("organizations.detailsBasicInfo")}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoRow label={t("organizations.form.name")} value={org.name} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoRow
                label={t("organizations.form.type")}
                value={getOrganizationTypeLabel(org.organization_type)}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: APP_FONT_FAMILY,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "text.secondary",
                  letterSpacing: "0.02em",
                  mb: 0.5,
                }}
              >
                {t("organizations.form.industry")}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {industries.length > 0 ? (
                  industries.map((ind) => (
                    <Chip
                      key={ind}
                      label={ind}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: "8px" }}
                    />
                  ))
                ) : (
                  <Typography sx={{ fontFamily: APP_FONT_FAMILY }}>—</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon={<ContactMailOutlinedIcon fontSize="small" />}
          title={t("organizations.detailsContact")}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoRow label={t("organizations.form.contactEmail")} value={org.data?.email} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoRow label={t("organizations.form.phoneNumber")} value={org.data?.phone_number} />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard
          icon={<LocationOnOutlinedIcon fontSize="small" />}
          title={t("organizations.detailsLocation")}
        >
          <InfoRow
            label={t("organizations.form.googleMapsUrl")}
            value={org.google_maps_url}
          />
        </SectionCard>

        {org.data?.more_info && (
          <SectionCard
            icon={<BusinessOutlinedIcon fontSize="small" />}
            title={t("organizations.detailsNotes")}
          >
            <Typography
              sx={{
                fontFamily: APP_FONT_FAMILY,
                fontSize: "0.95rem",
                whiteSpace: "pre-wrap",
                color: "text.primary",
              }}
            >
              {org.data.more_info}
            </Typography>
          </SectionCard>
        )}
      </Stack>
    </Box>
  );
};

export default OrganizationDetails;
